import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { EditorSelectionContent } from "@/components/environments/EditorSelectionContent";
import { Counter } from "@/components/flexkit/badge/Counter";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useState, type FC, type ReactNode } from "react";

export type CodeTabButtonWithEditorDropdownProps = {
    id: string;
    label: string;
    icon: ReactNode;
    isActive: boolean;
    onClick: () => void;
    panelId: string;
    badge?: number;
    hideLabel?: boolean;
    "data-tracking-id": string;
    /** The environment to get editor options for */
    environment?: PlainEnvironment;
    /** Whether the editor dropdown should be disabled (e.g., environment not running) */
    editorDropdownDisabled?: boolean;
};

/**
 * A tab button for the Code tab that includes an editor selection dropdown.
 * The main button area selects the Code tab, while the chevron opens the editor dropdown.
 */
export const CodeTabButtonWithEditorDropdown: FC<CodeTabButtonWithEditorDropdownProps> = ({
    id,
    label,
    icon,
    isActive,
    onClick,
    panelId,
    badge,
    hideLabel = false,
    "data-tracking-id": trackingId,
    environment,
    editorDropdownDisabled = false,
}) => {
    const { availableEditors, recommendedEditors, isSingleEditor } = useGetEditorForEnv(
        environment?.id,
        environment?.metadata?.projectId,
    );
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Show the dropdown chevron when there's an environment and multiple editors
    const showDropdown = environment && !isSingleEditor;

    const mainButton = (
        <button
            id={id}
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            aria-label={hideLabel ? label : undefined}
            onClick={onClick}
            className={cn(
                "flex items-center gap-1.5 py-1 text-base transition-colors",
                // When dropdown is shown, only round left corners
                showDropdown ? "rounded-l-md pl-3 pr-2" : "rounded-md px-3",
                isActive
                    ? "bg-surface-button-tab-primary text-content-primary shadow-sm"
                    : "text-content-secondary hover:text-content-primary",
            )}
            data-tracking-id={trackingId}
        >
            {icon}
            {!hideLabel && label}
            {badge !== undefined && badge > 0 && <Counter counter={String(badge)} className="text-xs" />}
        </button>
    );

    const wrappedMainButton = hideLabel ? <Tooltip content={label}>{mainButton}</Tooltip> : mainButton;

    if (!showDropdown) {
        return wrappedMainButton;
    }

    const tooltipContent = editorDropdownDisabled ? "Start environment to open in editor" : "Open in editor";

    return (
        <div className="flex items-stretch">
            {wrappedMainButton}
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                <Tooltip content={tooltipContent}>
                    <DropdownMenuTrigger asChild disabled={editorDropdownDisabled}>
                        <button
                            aria-label="Open editor selection"
                            disabled={editorDropdownDisabled}
                            className={cn(
                                "flex items-center justify-center rounded-r-md pl-1 pr-2 text-base transition-colors",
                                "disabled:cursor-not-allowed disabled:opacity-50",
                                isActive
                                    ? [
                                          "bg-surface-button-tab-primary text-content-primary shadow-sm",
                                          "hover:bg-surface-button-tab-primary-accent",
                                          isDropdownOpen && "bg-surface-button-tab-primary-accent",
                                      ]
                                    : [
                                          "text-content-secondary",
                                          "hover:bg-surface-tertiary hover:text-content-primary",
                                          isDropdownOpen && "bg-surface-tertiary text-content-primary",
                                      ],
                            )}
                            data-tracking-id="code-tab-editor-dropdown"
                            data-testid="code-tab-editor-dropdown"
                        >
                            <IconChevronDownSmall size="sm" />
                        </button>
                    </DropdownMenuTrigger>
                </Tooltip>
                <DropdownMenuPortal>
                    <DropdownMenuContent
                        align="end"
                        className="w-auto min-w-64 transition-all duration-200"
                        data-track-location={TrackLocations.EnvironmentEditorPopover}
                    >
                        <EditorSelectionContent
                            environment={environment}
                            editors={availableEditors || []}
                            recommendedEditors={recommendedEditors}
                            hideDefaultEditorUI
                        />
                    </DropdownMenuContent>
                </DropdownMenuPortal>
            </DropdownMenu>
        </div>
    );
};
